"use client";

import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import Script from "next/script";
import NextImage from "@components/Image/NextImage";
import PrivacyCookiePopup from "@components/Header/PrivacyCookiePopup";
import { useAppContext } from "@components/Provider";
import { RenderHomePageSchema } from "helpers/Helper";
import EBBHomepageSignupForm from "./EBBHomepageSignupForm";
import {
  extractHomepageClosing,
  extractHomepageDeals,
  extractHomepageFeatures,
  extractHomepageMedia,
  extractHomepageSignup,
  extractHomepageTestimonials,
  formatBadgePrice,
  getHomepagePost,
} from "./ebbHomepageUtils";
import styles from "./EBBHomepage.module.css";

const Modal = dynamic(() => import("@components/Modal/Modal"), { ssr: false });
const Login = dynamic(() => import("@components/Login/Login"), { ssr: false });
const ForgotPassword = dynamic(() => import("@components/ForgotPassword/ForgotPassword"), {
  ssr: false,
});

const renderAccentText = (text) => {
  if (!text) return null;
  return String(text)
    .split(/(\*[^*]+\*)/g)
    .map((part, index) => {
      const match = part.match(/^\*([^*]+)\*$/);
      if (match) {
        return (
          <span key={index} className={styles.accent}>
            {match[1]}
          </span>
        );
      }
      return part;
    });
};

const ArtImage = ({ desktopSrc, mobileSrc, className, alt = "", fetchPriority }) => {
  if (!desktopSrc && !mobileSrc) return null;
  if (desktopSrc && mobileSrc && desktopSrc !== mobileSrc) {
    return (
      <picture>
        <source media="(min-width: 768px)" srcSet={desktopSrc} />
        <img
          src={mobileSrc}
          alt={alt}
          className={className}
          fetchPriority={fetchPriority}
        />
      </picture>
    );
  }
  return (
    <img
      src={desktopSrc || mobileSrc}
      alt={alt}
      className={className}
      fetchPriority={fetchPriority}
    />
  );
};

const BookCover = ({ book, className, priority = false, onSignupClick }) => {
  const href = book.link || "";
  const cover = (
    <div className={`${styles.bookCover} ${className || ""}`}>
      {book.price ? <span className={styles.priceBadge}>{formatBadgePrice(book.price)}</span> : null}
      <NextImage
        ImageUrl={book.imageUrl}
        Alt_Text={book.title || "Ebook cover"}
        Title={book.title}
        width={120}
        height={180}
        isLazyload={!priority}
        fetchpriority={priority}
        style={{ width: "100%", height: "auto" }}
      />
    </div>
  );

  if (href) {
    return (
      <a href={href} className={styles.bookLink}>
        {cover}
      </a>
    );
  }
  if (onSignupClick) {
    return (
      <button type="button" className={styles.bookLink} onClick={onSignupClick}>
        {cover}
      </button>
    );
  }
  return cover;
};

const DealsCarousel = ({ books, onBookSignup }) => {
  const scrollerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const slides = books;

  const getStep = () => {
    const node = scrollerRef.current;
    if (!node?.firstElementChild) return 0;
    const gap = parseFloat(window.getComputedStyle(node).gap) || 16;
    return node.firstElementChild.getBoundingClientRect().width + gap;
  };

  const syncPage = () => {
    const node = scrollerRef.current;
    const step = getStep();
    if (!node || !step) return;
    setPage(Math.round(node.scrollLeft / step));
  };

  const scrollToPage = (index) => {
    const node = scrollerRef.current;
    const step = getStep();
    if (!node || !step) return;
    node.scrollTo({ left: step * index, behavior: "smooth" });
    setPage(index);
  };

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return undefined;
    node.addEventListener("scroll", syncPage, { passive: true });
    return () => node.removeEventListener("scroll", syncPage);
  }, [slides.length]);

  useEffect(() => {
    if (paused || slides.length < 2) return undefined;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const desktop = window.matchMedia("(min-width: 768px)").matches;
    if (reduceMotion || !desktop) return undefined;

    const id = window.setInterval(() => {
      const node = scrollerRef.current;
      const step = getStep();
      if (!node || !step) return;
      const maxScroll = node.scrollWidth - node.clientWidth;
      const nextLeft = node.scrollLeft + step;
      node.scrollTo({
        left: nextLeft >= maxScroll - 8 ? 0 : nextLeft,
        behavior: "smooth",
      });
    }, 4000);

    return () => window.clearInterval(id);
  }, [paused, slides.length]);

  return (
    <div
      className={styles.carouselWrap}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <div className={styles.carouselTrack} ref={scrollerRef}>
        {slides.map((book, index) => (
          <div className={styles.carouselCard} key={`${book.isbn || book.title}-${index}`}>
            <BookCover book={book} onSignupClick={onBookSignup} />
          </div>
        ))}
      </div>
      <div className={styles.dots} role="tablist" aria-label="Recent ebook deals">
        {slides.map((book, index) => (
          <button
            key={`deal-dot-${index}`}
            type="button"
            className={`${styles.dot} ${page === index ? styles.dotActive : ""}`}
            aria-label={`Show deal ${index + 1}`}
            onClick={() => scrollToPage(index)}
          />
        ))}
      </div>
    </div>
  );
};

const StarRow = () => (
  <div className={styles.stars} aria-hidden="true">
    {Array.from({ length: 5 }).map((_, index) => (
      <span key={index}>★</span>
    ))}
  </div>
);

const Testimonials = ({ quotes }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (quotes.length < 2) {
      return undefined;
    }
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setPage((current) => (current + 1) % quotes.length);
    }, 5000);
    return () => window.clearInterval(id);
  }, [quotes.length]);

  return (
    <section className={styles.testimonials}>
      <div className={styles.testimonialsDesktop}>
        {quotes.map((quote, index) => (
          <div className={styles.testimonialCard} key={`quote-desktop-${index}`}>
            <StarRow />
            <p>{quote.text}</p>
          </div>
        ))}
      </div>
      <div className={styles.testimonialsMobile}>
        <div className={styles.testimonialCard}>
          <StarRow />
          <p>{quotes[page]?.text}</p>
        </div>
        <div className={styles.dots} role="tablist" aria-label="Reviews">
          {quotes.map((_, index) => (
            <button
              key={`quote-dot-${index}`}
              type="button"
              className={`${styles.dot} ${page === index ? styles.dotActive : ""}`}
              aria-label={`Show review ${index + 1}`}
              onClick={() => setPage(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const HomepageSignupModal = ({
  isOpen,
  onClose,
  formId,
  headline,
  signUpLocation,
  newsletterProps,
  Newsletter_Settings,
  postType,
}) => (
  <Modal
    isOpen={isOpen}
    closeBtn={onClose}
    sizeClass="small"
    className={styles.signupModalShell}
    transitionName="modal-anim"
  >
    {headline ? <h2 className={styles.signupModalHeadline}>{headline}</h2> : null}
    <EBBHomepageSignupForm
      variant="modal"
      formId={formId}
      signUpLocation={signUpLocation}
      newsletterProps={newsletterProps}
      Newsletter_Settings={Newsletter_Settings}
      postType={postType}
    />
  </Modal>
);

const HomepageHeader = ({
  siteLogo,
  siteName,
  showStickySignup,
  onCtaClick,
  headline,
  ctaText,
  signUpLocation,
  pageNewsletter,
  Newsletter_Settings,
  postType,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [privacyCookie, setPrivacyCookie] = useState("hidden");
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const { siteId, data, tokenCookie } = useAppContext();

  useEffect(() => {
    if (localStorage.getItem("cookiePopup")) setPrivacyCookie("shown");
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {privacyCookie !== "shown" && (
        <PrivacyCookiePopup setPrivacyCookie={setPrivacyCookie} siteId={siteId} data={data} />
      )}
      <header
        className={`${styles.header} ${scrolled || showStickySignup ? styles.headerScrolled : ""} ${
          showStickySignup ? styles.headerSticky : ""
        }`}
        data-ebb-homepage-header="true"
      >
        <div className={styles.headerRow} data-ebb-homepage-header-row="true">
          <a href="/" className={styles.logoWrap} aria-label="Early Bird Books home">
            {siteLogo ? (
              <NextImage
                ImageUrl={siteLogo}
                Title={siteName || "Early Bird Books"}
                Alt_Text={siteName || "Early Bird Books"}
                className={styles.logoImage}
                width={220}
                height={40}
                isLazyload={false}
                fetchpriority={true}
              />
            ) : (
              <span className={styles.logoFallback}>EARLY BIRD BOOKS</span>
            )}
          </a>
          {!tokenCookie ? (
            <button type="button" className={styles.loginBtn} onClick={() => setIsLoginOpen(true)}>
              LOG IN
            </button>
          ) : (
            <Link href="/account/profile" className={styles.loginBtn}>
              ACCOUNT
            </Link>
          )}
        </div>
        <div
          className={`${styles.stickySignup} ${showStickySignup ? styles.stickySignupOpen : ""}`}
          aria-hidden={!showStickySignup}
          inert={showStickySignup ? undefined : true}
        >
          <div className={styles.stickySignupInner}>
            {headline ? <p className={styles.stickyHeadline}>{headline}</p> : null}
            <div className={styles.stickyDesktopForm}>
              <EBBHomepageSignupForm
                variant="sticky"
                formId="homepage-sticky-form"
                signUpLocation={signUpLocation}
                newsletterProps={pageNewsletter}
                Newsletter_Settings={Newsletter_Settings}
                postType={postType}
              />
            </div>
            {ctaText ? (
              <button
                type="button"
                className={styles.stickyMobileCta}
                onClick={onCtaClick}
                tabIndex={showStickySignup ? 0 : -1}
              >
                {ctaText}
              </button>
            ) : null}
          </div>
        </div>
      </header>
      <Modal isOpen={isLoginOpen} closeBtn={() => setIsLoginOpen(false)} className="LoginModal">
        <Login
          openForgotPasswordModal={() => {
            setIsLoginOpen(false);
            setIsForgotPasswordOpen(true);
          }}
          closeLoginModal={() => setIsLoginOpen(false)}
          buttonText="LOGIN"
          classNames="button"
          hidePlaceHolder={true}
        />
      </Modal>
      <Modal
        isOpen={isForgotPasswordOpen}
        closeBtn={() => setIsForgotPasswordOpen(false)}
        className="ForgotPassword"
      >
        <ForgotPassword />
      </Modal>
      <style jsx global>{`
        .LoginModal .medium-modal.modal {
          width: 45%;
          padding: 2rem;
          border-radius: 10px;
        }
        @media (max-width: 1024px) {
          .LoginModal .medium-modal.modal {
            width: 90%;
          }
        }
      `}</style>
    </>
  );
};

const EBBHomepage = (props) => {
  const { data, host: contextHost, siteId: contextSiteId } = useAppContext();
  const post = getHomepagePost(props);
  const host = contextHost || props.host;
  const siteId = contextSiteId || props.siteId;
  const siteSettings = data?.SiteSettings || {};
  const {
    header: signupHeader,
    modalHeader: signupModalHeader,
    buttonText: ctaText,
    signUpLocation,
    pageNewsletter,
    isModal: isModalSignup,
    newsletterProps,
    dealsNewsletter,
    dealsModalHeader,
  } = extractHomepageSignup(post);
  const { heroDesktop, heroMobile } = extractHomepageMedia({
    heroDesktopFile: props.ebbHeroBannerDesktop,
    heroMobileFile: props.ebbHeroBannerMobile,
  });
  const features = extractHomepageFeatures(post);
  const { header: dealsHeader, subheader: dealsSubheader, books } = extractHomepageDeals(post);
  const quotes = extractHomepageTestimonials(post);
  const closing = extractHomepageClosing(post);
  const siteLogo =
    siteSettings.primary_site_logo_svg_black ||
    siteSettings.primary_site_logo_svg ||
    siteSettings.primary_site_logo;
  const siteName = siteSettings.site_name || "Early Bird Books";
  const heroRef = useRef(null);
  const closingRef = useRef(null);
  const [heroPast, setHeroPast] = useState(false);
  const [closingInView, setClosingInView] = useState(false);
  const [signupModal, setSignupModal] = useState(null);
  const showStickySignup = heroPast && !closingInView;
  const featureClassNames = [styles.featureBargain, styles.featureGenres, styles.featureRetailers];

  useEffect(() => {
    const hero = heroRef.current;
    const closing = closingRef.current;
    if (!hero) return undefined;

    let frame = 0;

    const update = () => {
      frame = 0;
      const headerRow = document.querySelector("[data-ebb-homepage-header-row]");
      const headerBottom = headerRow ? headerRow.getBoundingClientRect().bottom : 64;
      const heroCard = hero.querySelector("[data-ebb-hero-card]");
      const pastHero = (heroCard || hero).getBoundingClientRect().bottom <= headerBottom + 8;
      const closingTop = closing ? closing.getBoundingClientRect().top : Infinity;
      const arrivedAtClosing = closingTop < Math.max(headerBottom + 48, window.innerHeight * 0.4);
      setHeroPast(pastHero);
      setClosingInView(arrivedAtClosing);
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("resize", scheduleUpdate);
    window.visualViewport?.addEventListener("scroll", scheduleUpdate);

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    resizeObserver.observe(hero);
    resizeObserver.observe(document.documentElement);
    if (closing) resizeObserver.observe(closing);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("resize", scheduleUpdate);
      window.visualViewport?.removeEventListener("scroll", scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, []);

  const scrollToSignup = () => {
    const target = document.getElementById("homepage-signup");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const onCtaClick = () => {
    if (isModalSignup) {
      setSignupModal("page");
      return;
    }
    scrollToSignup();
  };

  return (
    <div className={styles.page}>
      <HomepageHeader
        siteLogo={siteLogo}
        siteName={siteName}
        showStickySignup={showStickySignup}
        onCtaClick={onCtaClick}
        headline={signupHeader}
        ctaText={ctaText}
        signUpLocation={signUpLocation}
        pageNewsletter={pageNewsletter}
        Newsletter_Settings={siteSettings.newsletter_settings}
        postType={post?.post_type}
      />

      <section className={styles.hero} ref={heroRef}>
        <ArtImage
          desktopSrc={heroDesktop}
          mobileSrc={heroMobile}
          className={styles.heroBanner}
          fetchPriority="high"
        />
        <div className={styles.heroCard} data-ebb-hero-card="true">
          {signupHeader ? <h1>{signupHeader}</h1> : null}
          <EBBHomepageSignupForm
            variant="hero"
            formId="homepage-hero-form"
            signUpLocation={signUpLocation}
            newsletterProps={pageNewsletter}
            Newsletter_Settings={siteSettings.newsletter_settings}
            postType={post?.post_type}
          />
        </div>
      </section>

      {features.length > 0 ? (
        <section className={styles.features}>
          {features.map((feature, index) => (
            <article
              className={`${styles.feature} ${featureClassNames[index] || ""}`}
              key={`feature-${index}`}
            >
              {feature.imageUrl ? (
                <div className={styles.featureVisual}>
                  <img src={feature.imageUrl} alt="" className={styles.pointImage} />
                </div>
              ) : null}
              {feature.heading ? <h2>{renderAccentText(feature.heading)}</h2> : null}
              {feature.body ? <p>{feature.body}</p> : null}
            </article>
          ))}
        </section>
      ) : null}

      {ctaText ? (
        <div className={styles.ctaRow}>
          <button type="button" className={styles.coralCta} onClick={onCtaClick}>
            {ctaText}
          </button>
        </div>
      ) : null}

      {books.length > 0 ? (
        <section className={styles.deals}>
          {dealsHeader ? <h2>{dealsHeader}</h2> : null}
          {dealsSubheader ? <p className={styles.dealsSub}>{dealsSubheader}</p> : null}
          <DealsCarousel
            books={books}
            onBookSignup={dealsNewsletter ? () => setSignupModal("deals") : undefined}
          />
        </section>
      ) : null}

      {ctaText ? (
        <div className={styles.ctaRow}>
          <button type="button" className={styles.coralCta} onClick={onCtaClick}>
            {ctaText}
          </button>
        </div>
      ) : null}

      {quotes.length > 0 ? <Testimonials quotes={quotes} /> : null}

      <section className={styles.closing} id="homepage-signup" ref={closingRef}>
        <ArtImage
          desktopSrc={closing.imageDesktop}
          mobileSrc={closing.imageMobile}
          className={styles.closingBanner}
          alt=""
        />
        {closing.header ? <h2>{renderAccentText(closing.header)}</h2> : null}
        <EBBHomepageSignupForm
          variant="closing"
          formId="homepage-closing-form"
          signUpLocation={signUpLocation}
          newsletterProps={pageNewsletter}
          Newsletter_Settings={siteSettings.newsletter_settings}
          postType={post?.post_type}
        />
      </section>
      {isModalSignup ? (
        <HomepageSignupModal
          isOpen={signupModal === "page"}
          onClose={() => setSignupModal(null)}
          formId="homepage-modal-form"
          headline={signupModalHeader || signupHeader}
          signUpLocation={signUpLocation}
          newsletterProps={newsletterProps}
          Newsletter_Settings={siteSettings.newsletter_settings}
          postType={post?.post_type}
        />
      ) : null}
      {dealsNewsletter ? (
        <HomepageSignupModal
          isOpen={signupModal === "deals"}
          onClose={() => setSignupModal(null)}
          formId="homepage-deals-modal-form"
          headline={dealsModalHeader || signupModalHeader || signupHeader}
          signUpLocation={dealsNewsletter.signUpLocation || signUpLocation}
          newsletterProps={dealsNewsletter}
          Newsletter_Settings={siteSettings.newsletter_settings}
          postType={post?.post_type}
        />
      ) : null}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={RenderHomePageSchema(siteSettings, post, host, siteId)}
      />
      <Script
        strategy="lazyOnload"
        type="text/javascript"
        data-cfasync="false"
        dangerouslySetInnerHTML={{
          __html: `
            var blogherads = blogherads || {};
            blogherads.adq = blogherads.adq || [];
            blogherads.adq.push(function() {
              blogherads.disableAds();
            });
          `,
        }}
      />
    </div>
  );
};

export default EBBHomepage;
